import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Users, Mail, Phone, Lock } from "lucide-react";
import GlassCard from "../../components/ui-custom/GlassCard";
import Reveal from "../../components/ui-custom/Reveal";
import { Button } from "../../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Directory = () => {
  const [people, setPeople] = useState(null);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/employees/directory`, { withCredentials: true })
      .then((res) => setPeople(res.data.items || []))
      .catch((err) => {
        if (err.response?.status === 403) {
          setNeedsProfile(true);
        } else {
          const detail = err.response?.data?.detail;
          const message = Array.isArray(detail) ? detail[0]?.msg : detail;
          setError(message || err.message || "Could not load the Employee Directory.");
        }
      });
  }, []);

  if (needsProfile) {
    return (
      <Reveal>
        <GlassCard className="p-10 text-center max-w-lg mx-auto">
          <span className="w-14 h-14 rounded-full bg-qcyan/10 border border-qcyan/25 flex items-center justify-center mx-auto mb-5">
            <Lock size={24} className="text-qcyan" />
          </span>
          <p className="text-foreground font-medium mb-2">Complete your profile to unlock this page.</p>
          <p className="text-sm text-muted-foreground mb-6">
            Add your Employee Code and a profile photo so colleagues know who they're looking at.
          </p>
          <Link to="/employees/profile">
            <Button className="btn-glow-primary">Complete My Profile</Button>
          </Link>
        </GlassCard>
      </Reveal>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25">
          <Users size={20} className="text-qcyan" />
        </span>
        <h2 className="font-display font-semibold text-2xl text-foreground">Employee Directory</h2>
      </div>

      {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
      {!error && people === null && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!error && people !== null && people.length === 0 && (
        <p className="text-sm text-muted-foreground">No other employees have completed their profile yet.</p>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {people?.map((p) => (
          <Reveal key={p.employee_code + p.email}>
            <GlassCard className="p-6">
              <div className="flex items-center gap-3 mb-3">
                {p.photo_data_url && (
                  <img
                    src={p.photo_data_url}
                    alt={p.name}
                    className="w-11 h-11 rounded-full object-cover border border-border/20 flex-none"
                  />
                )}
                <div>
                  <h3 className="font-display font-semibold text-foreground">{p.name}</h3>
                  {p.role && <p className="text-sm text-qcyan">{p.role}</p>}
                </div>
              </div>
              {p.department && <p className="text-xs text-muted-foreground mb-3">{p.department}</p>}
              <p className="text-xs text-muted-foreground mb-3">Code: {p.employee_code}</p>
              <div className="space-y-1 text-xs text-muted-foreground">
                {p.email && (
                  <p className="flex items-center gap-1.5">
                    <Mail size={12} /> {p.email}
                  </p>
                )}
                {p.phone && (
                  <p className="flex items-center gap-1.5">
                    <Phone size={12} /> {p.phone}
                  </p>
                )}
              </div>
            </GlassCard>
          </Reveal>
        ))}
      </div>
    </div>
  );
};

export default Directory;
